import cn from 'classnames';
import {ChangeEvent, ForwardedRef, forwardRef, InputHTMLAttributes, MouseEvent} from 'react';
import {CloseIcon} from 'shared/icons';
import {textControlClassName} from './textControlClassName';
import styles from './TextInput.module.css';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> & {
	className?: string;
	/** Показать крестик очистки при непустом value */
	onClear?: () => void;
	clearAriaLabel?: string;
	/** Многострочный ввод с переносом длинного текста */
	multiline?: boolean;
};

export const TextInput = forwardRef<HTMLInputElement | HTMLTextAreaElement, Props>(function TextInput(
	{className, onClear, clearAriaLabel = 'Очистить', multiline = false, value, onChange, ...props},
	ref,
) {
	const hasValue = value !== undefined && value !== null && String(value).length > 0;
	const showClear = Boolean(onClear) && hasValue;

	const handleClearClick = (event: MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		event.stopPropagation();
		onClear?.();
	};

	const handleTextAreaChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
		onChange?.(event as unknown as ChangeEvent<HTMLInputElement>);
	};

	return (
		<div className={cn(styles.root, multiline && styles.rootMultiline)}>
			{multiline ? (
				<textarea
					ref={ref as ForwardedRef<HTMLTextAreaElement>}
					className={cn(textControlClassName, styles.control, styles.textarea, className)}
					value={value}
					rows={2}
					wrap="soft"
					onChange={handleTextAreaChange}
					name={props.name}
					placeholder={props.placeholder}
					autoComplete={props.autoComplete}
					disabled={props.disabled}
					aria-invalid={props['aria-invalid']}
				/>
			) : (
				<input
					ref={ref as ForwardedRef<HTMLInputElement>}
					className={cn(textControlClassName, styles.control, styles.input, className)}
					value={value}
					onChange={onChange}
					{...props}
				/>
			)}
			{showClear ? (
				<button
					type="button"
					className={styles.clearButton}
					aria-label={clearAriaLabel}
					tabIndex={-1}
					onClick={handleClearClick}
				>
					<CloseIcon className={styles.clearIcon} />
				</button>
			) : null}
		</div>
	);
});
